/**
 * Browse-by-{cadence,persona,cut} library views (M3X.UIPOLISH PR 11).
 *
 * Single page powers /reports/library/by-cadence,
 * /reports/library/by-persona, /reports/library/by-cut. Each route
 * picks a different axis from the REPORTS_REGISTRY.
 */

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import {
  EmptyState,
  PageHeader,
  SettingsEntryCard,
  SettingsSection,
} from "../../components/ui";
import {
  REPORTS_REGISTRY,
  reportsByCadence,
  reportsByCut,
  reportsByPersona,
} from "../../data/reports_registry";
import type {
  ReportCadence,
  ReportCut,
  ReportDef,
  ReportPersona,
} from "../../data/reports_registry";

type Axis = "cadence" | "persona" | "cut";

const CADENCE_BUCKETS: ReportCadence[] = [
  "real_time",
  "daily",
  "weekly",
  "monthly",
  "quarterly",
  "annual",
  "on_demand",
];
const PERSONA_BUCKETS: ReportPersona[] = [
  "hr_director",
  "hr_partner",
  "manager",
  "finance",
  "compliance_officer",
  "ceo_chro",
  "employee",
];
const CUT_BUCKETS: ReportCut[] = [
  "per_employee",
  "per_department",
  "per_project",
  "per_grade",
  "per_branch",
  "per_nationality",
  "cross_le",
  "global",
];

function bucketsFor(axis: Axis): readonly string[] {
  if (axis === "cadence") return CADENCE_BUCKETS;
  if (axis === "persona") return PERSONA_BUCKETS;
  return CUT_BUCKETS;
}

function reportsForBucket(axis: Axis, bucket: string): ReportDef[] {
  if (axis === "cadence") return reportsByCadence(bucket as ReportCadence);
  if (axis === "persona") return reportsByPersona(bucket as ReportPersona);
  return reportsByCut(bucket as ReportCut);
}

export function LibraryByCutPage() {
  const { t } = useTranslation();
  const { axis } = useParams<{ axis: Axis }>();
  const [searchTerm, setSearchTerm] = useState("");

  const axisLabel =
    axis === "cadence"
      ? t("reports_lib.axis_cadence", "Cadence")
      : axis === "persona"
        ? t("reports_lib.axis_persona", "Persona")
        : axis === "cut"
          ? t("reports_lib.axis_cut", "Cut")
          : t("reports_lib.title", "Reports");

  const validAxis: Axis = ["cadence", "persona", "cut"].includes(axis ?? "")
    ? (axis as Axis)
    : "cadence";

  const buckets = bucketsFor(validAxis);
  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return REPORTS_REGISTRY;
    const needle = searchTerm.toLowerCase();
    return REPORTS_REGISTRY.filter(
      (r) =>
        r.id.toLowerCase().includes(needle) ||
        t(r.titleKey, r.id).toLowerCase().includes(needle),
    );
  }, [searchTerm, t]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t("reports_lib.browse_by", "Browse by {{axis}}", {
          axis: axisLabel,
        })}
        breadcrumbs={[
          { label: t("reports_lib.title", "Reports"), href: "/reports" },
          { label: axisLabel },
        ]}
      />

      <div className="relative max-w-md">
        <input
          type="search"
          placeholder={t(
            "reports_lib.search_all_reports",
            "Search all reports…",
          )}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full rounded-lg border border-border-subtle bg-surface-default px-3 py-2 text-sm placeholder:text-text-tertiary focus:border-ai focus:outline-none focus:ring-2 focus:ring-ai/20"
          aria-label={t(
            "reports_lib.search_all_reports",
            "Search all reports",
          )}
        />
      </div>

      {buckets.map((bucket) => {
        const inBucket = reportsForBucket(validAxis, bucket);
        const matching = filtered.filter((r) =>
          inBucket.some((b) => b.id === r.id),
        );
        if (matching.length === 0) return null;
        return (
          <SettingsSection
            key={bucket}
            title={t(`reports_lib.${validAxis}_${bucket}`, bucket)}
          >
            {matching.map((r) => (
              <SettingsEntryCard
                key={r.id}
                icon={r.icon}
                title={t(r.titleKey, r.id)}
                description={t(r.descriptionKey, "")}
                to={r.to}
                meta={`${t(`reports_lib.cadence_${r.cadence}`, r.cadence)} · ${t(`reports_lib.persona_${r.persona}`, r.persona)}`}
                tone="indigo"
                comingSoon={r.comingSoon}
              />
            ))}
          </SettingsSection>
        );
      })}

      {filtered.length === 0 && (
        <EmptyState
          headline={t("reports_lib.no_match", "No reports match your search")}
        />
      )}
    </div>
  );
}

export default LibraryByCutPage;
